import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search, Clock, ArrowRight, Sparkles, BookOpen, Plus, ShieldCheck, Filter, Play, Video, Film, FileText, MessageSquare } from "lucide-react";
import { api, SITE_URL } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/AdSlot";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["Todas", "Crítica", "Reseña", "Lista", "Ensayo", "Entrevista"];

export default function RevistaList() {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const isAdminOrEditor = user?.role === "admin" || user?.role === "editor";

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("Todas");
  const [searchQuery, setSearchQuery] = useState("");
  const [activePlayingVideo, setActivePlayingVideo] = useState(null);

  useEffect(() => {
    api.get("/articles", { params: { limit: 50 } })
      .then(({ data }) => setArticles(data.articles || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  // Multilingual helper to extract localized fields
  const loc = (art, field) => {
    if (!art) return "";
    if (lang === "en" && art[`${field}_en`]) return art[`${field}_en`];
    if (lang === "fr" && art[`${field}_fr`]) return art[`${field}_fr`];
    return art[field] || "";
  };

  const getSlugLink = (art) => {
    if (!art) return "/revista";
    const slug = (lang === "en" && art.slug_en) || (lang === "fr" && art.slug_fr) || art.slug;
    return `/revista/${slug}${lang !== "es" ? `?lang=${lang}` : ""}`;
  };

  const filteredArticles = useMemo(() => {
    return articles.filter((a) => {
      const matchesCat = activeCategory === "Todas" || a.category === activeCategory;
      const q = searchQuery.toLowerCase().trim();
      const title = loc(a, "title").toLowerCase();
      const excerpt = loc(a, "excerpt").toLowerCase();
      const author = (a.author_name || "").toLowerCase();
      const matchesSearch = !q || title.includes(q) || excerpt.includes(q) || author.includes(q);
      return matchesCat && matchesSearch;
    });
  }, [articles, activeCategory, searchQuery, lang, loc]);

  // Section categorization
  const featuredArticle = filteredArticles[0];
  const videoArticles = useMemo(() => filteredArticles.filter(a => a.video_url), [filteredArticles]);
  const reviewsAndEssays = useMemo(() => filteredArticles.filter(a => a.category === "Crítica" || a.category === "Ensayo" || a.category === "Reseña"), [filteredArticles]);
  const interviews = useMemo(() => filteredArticles.filter(a => a.category === "Entrevista"), [filteredArticles]);
  const restArticles = useMemo(() => filteredArticles.slice(1), [filteredArticles]);

  const getReadTime = (text = "") => {
    const words = text.replace(/<[^>]+>/g, "").split(/\s+/).length;
    return `${Math.max(3, Math.ceil(words / 200))} ${t("minRead")}`;
  };

  // Structured Data & SEO
  const canonicalUrl = `${SITE_URL}/revista${lang !== "es" ? `?lang=${lang}` : ""}`;
  
  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col" data-testid="revista-list">
      <Helmet>
        <title>{`${t("magazine")} — ${t("tagline")}`}</title>
        <meta name="description" content={t("taglineSub")} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Multilingual Hreflang Links for SEO */}
        <link rel="alternate" hrefLang="es" href={`${SITE_URL}/revista`} />
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}/revista?lang=en`} />
        <link rel="alternate" hrefLang="fr" href={`${SITE_URL}/revista?lang=fr`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/revista`} />
        <link rel="alternate" type="application/rss+xml" title="Revista La Caja LGBT — Feed RSS" href={`${SITE_URL}/rss.xml`} />

        {/* Open Graph Meta */}
        <meta property="og:title" content={`${t("magazine")} — La Caja LGBT`} />
        <meta property="og:description" content={t("taglineSub")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        {featuredArticle?.cover_url && <meta property="og:image" content={featuredArticle.cover_url} />}
      </Helmet>

      <Navbar />

      <main className="flex-1 pt-24 sm:pt-28 pb-16 px-4 sm:px-6 md:px-12 lg:px-16 max-w-7xl mx-auto w-full">
        {/* Admin / Editor Management Banner */}
        {isAdminOrEditor && (
          <div
            className="mb-8 p-4 rounded-xl bg-gradient-to-r from-[#E11D48]/20 via-zinc-900 to-zinc-900 border border-[#E11D48]/40 flex flex-wrap items-center justify-between gap-4 shadow-lg"
            data-testid="admin-editor-magazine-bar"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E11D48]/20 border border-[#E11D48]/50 flex items-center justify-center text-[#E11D48] shrink-0">
                <ShieldCheck size={20} />
              </div>
              <div>
                <p className="text-sm font-semibold text-white flex items-center gap-2">
                  Gestión Editorial Activa
                  <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-[#E11D48] text-white">
                    {user.role}
                  </span>
                </p>
                <p className="text-xs text-zinc-400">
                  Redacta, edita o traduce artículos con portadas de video en ES, EN y FR.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/revista-editor"
                data-testid="admin-write-new-btn"
                className="bg-[#E11D48] hover:bg-[#BE123C] text-white px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all shadow-md"
              >
                <Plus size={15} /> {t("write")}
              </Link>
            </div>
          </div>
        )}

        {/* Clean Magazine Header Section (No redundant "Revista · Críticas y artículos" header) */}
        <div className="mb-10 border-b border-white/10 pb-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-[#E11D48] animate-ping" />
                <span className="text-xs tracking-[0.25em] uppercase font-bold text-[#E11D48]">
                  {t("magazine")}
                </span>
              </div>
              <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white uppercase">
                REVISTA DIGITAL
              </h1>
              <p className="text-sm sm:text-base text-zinc-400 mt-2 max-w-2xl leading-relaxed">
                Críticas, ensayos culturales, entrevistas y reportajes en video sobre cine y producciones LGBTQ+.
              </p>
            </div>

            {/* Magazine Search Input */}
            <div className="w-full sm:w-72 bg-zinc-900/90 border border-white/15 focus-within:border-[#E11D48] rounded-full px-4 py-2.5 flex items-center gap-2 transition-all shadow-lg">
              <Search size={16} className="text-zinc-400 shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`${t("searchPlaceholder")}...`}
                className="bg-transparent text-xs sm:text-sm outline-none text-white placeholder:text-zinc-500 w-full"
                data-testid="magazine-search-input"
              />
            </div>
          </div>

          {/* Category Filter Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pt-6 pb-2" data-testid="magazine-categories">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mr-2 flex items-center gap-1 shrink-0">
              <Filter size={12} /> {t("allArticles")}:
            </span>
            {CATEGORIES.map((cat) => {
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  data-testid={`category-tab-${cat}`}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all whitespace-nowrap shrink-0 ${
                    isActive
                      ? "bg-[#E11D48] text-white shadow-md shadow-[#E11D48]/30 scale-105"
                      : "bg-zinc-900 border border-white/10 text-zinc-400 hover:text-white hover:border-white/25"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="py-24 flex flex-col items-center justify-center gap-3">
            <div className="w-10 h-10 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin" />
            <p className="text-xs text-zinc-500 tracking-wider uppercase">Cargando la revista...</p>
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="py-20 text-center bg-zinc-900/30 rounded-2xl border border-white/10 p-8 my-6">
            <p className="text-lg font-medium text-zinc-300">{t("noArticles")}</p>
            <button
              onClick={() => { setActiveCategory("Todas"); setSearchQuery(""); }}
              className="mt-4 px-5 py-2.5 bg-[#E11D48] text-white rounded-full text-xs font-semibold hover:bg-[#BE123C] transition-colors"
            >
              Ver todas las ediciones
            </button>
          </div>
        ) : (
          <div className="space-y-16">
            {/* SECTION 1: COVER STORY (EDICIÓN DE PORTADA WITH VIDEO CAPABILITY) */}
            {featuredArticle && (
              <section className="space-y-4" data-testid="cover-story-section">
                <div className="flex items-center gap-2 border-l-4 border-[#E11D48] pl-3">
                  <Sparkles size={16} className="text-[#E11D48]" />
                  <h2 className="font-heading text-lg uppercase font-bold tracking-widest text-zinc-300">
                    {t("coverStory")}
                  </h2>
                </div>

                <div className="group relative rounded-3xl overflow-hidden bg-zinc-900/90 border border-white/15 hover:border-[#E11D48]/50 shadow-2xl transition-all duration-300">
                  <div className="grid grid-cols-1 lg:grid-cols-12 items-center">
                    {/* Media Column (Video vs Image) */}
                    <div className="lg:col-span-7 aspect-video lg:aspect-[16/10] overflow-hidden relative bg-black">
                      {activePlayingVideo === featuredArticle.id && featuredArticle.video_url ? (
                        <iframe
                          src={`${featuredArticle.video_url}${featuredArticle.video_url.includes("?") ? "&" : "?"}autoplay=1`}
                          title={loc(featuredArticle, "title")}
                          className="w-full h-full border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <>
                          {featuredArticle.cover_url ? (
                            <img
                              src={featuredArticle.cover_url}
                              alt={loc(featuredArticle, "title")}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                            />
                          ) : (
                            <div className="w-full h-full bg-zinc-800" />
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                          
                          {/* Video Overlay Badge if available */}
                          {featuredArticle.video_url && (
                            <button
                              onClick={() => setActivePlayingVideo(featuredArticle.id)}
                              className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 hover:bg-black/20 transition-all group/play cursor-pointer"
                              title={t("watchVideo")}
                            >
                              <div className="w-16 h-16 rounded-full bg-[#E11D48] text-white flex items-center justify-center shadow-2xl group-hover/play:scale-110 transition-transform">
                                <Play size={28} className="fill-white ml-1" />
                              </div>
                              <span className="mt-3 px-3 py-1 bg-black/80 text-white text-xs font-semibold rounded-full border border-white/20 backdrop-blur-md flex items-center gap-1.5">
                                <Video size={13} className="text-[#E11D48]" /> {t("watchVideo")}
                              </span>
                            </button>
                          )}
                        </>
                      )}

                      <div className="absolute top-4 left-4 z-10">
                        <span className="text-[11px] font-extrabold uppercase tracking-widest bg-[#E11D48] text-white px-3.5 py-1 rounded-full shadow-xl">
                          {featuredArticle.category}
                        </span>
                      </div>
                    </div>

                    {/* Content Column */}
                    <div className="lg:col-span-5 p-6 sm:p-10 flex flex-col justify-between h-full">
                      <div>
                        <div className="flex items-center gap-3 text-xs text-zinc-400 mb-4 font-medium">
                          <span className="text-zinc-200 font-semibold">{featuredArticle.author_name}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1 text-[#E11D48]">
                            <Clock size={13} /> {getReadTime(loc(featuredArticle, "content"))}
                          </span>
                        </div>

                        <Link to={getSlugLink(featuredArticle)}>
                          <h3 className="font-heading text-2xl sm:text-3xl lg:text-3xl font-bold text-white hover:text-[#E11D48] transition-colors leading-snug">
                            {loc(featuredArticle, "title")}
                          </h3>
                        </Link>

                        <p className="text-sm text-zinc-300 mt-4 line-clamp-3 leading-relaxed font-normal">
                          {loc(featuredArticle, "excerpt")}
                        </p>
                      </div>

                      <div className="mt-8 pt-4 border-t border-white/10 flex items-center justify-between">
                        <Link
                          to={getSlugLink(featuredArticle)}
                          className="inline-flex items-center gap-2 text-sm font-bold text-[#E11D48] hover:underline"
                        >
                          <span>{t("readMore")}</span>
                          <ArrowRight size={16} />
                        </Link>

                        {featuredArticle.video_url && (
                          <button
                            onClick={() => setActivePlayingVideo(activePlayingVideo === featuredArticle.id ? null : featuredArticle.id)}
                            className="text-xs font-semibold text-zinc-300 hover:text-white flex items-center gap-1.5 bg-zinc-800 px-3 py-1.5 rounded-full border border-white/10"
                          >
                            <Video size={14} className="text-[#E11D48]" /> {activePlayingVideo === featuredArticle.id ? "Cerrar video" : t("watchVideo")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* SECTION 2: VIDEO REPORTAJES & TRÁILERS (VIDEO COVERS SECTION) */}
            {videoArticles.length > 0 && (
              <section className="space-y-6" data-testid="video-articles-section">
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <div className="flex items-center gap-2">
                    <Video size={20} className="text-[#E11D48]" />
                    <h2 className="font-heading text-xl sm:text-2xl font-extrabold text-white">
                      {t("videoSection")}
                    </h2>
                  </div>
                  <span className="text-xs text-zinc-400 font-medium">Contenido audiovisual interactivo</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {videoArticles.map((art) => (
                    <div
                      key={art.id}
                      className="group bg-zinc-900/80 border border-white/15 hover:border-[#E11D48]/50 rounded-2xl overflow-hidden flex flex-col justify-between shadow-xl transition-all hover:-translate-y-1"
                    >
                      <div>
                        {/* Video Aspect Ratio Container */}
                        <div className="aspect-video w-full relative bg-black overflow-hidden">
                          {activePlayingVideo === art.id ? (
                            <iframe
                              src={`${art.video_url}${art.video_url.includes("?") ? "&" : "?"}autoplay=1`}
                              title={loc(art, "title")}
                              className="w-full h-full border-0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          ) : (
                            <>
                              {art.cover_url ? (
                                <img
                                  src={art.cover_url}
                                  alt={loc(art, "title")}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                              ) : (
                                <div className="w-full h-full bg-zinc-800" />
                              )}
                              <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center">
                                <button
                                  onClick={() => setActivePlayingVideo(art.id)}
                                  className="w-12 h-12 rounded-full bg-[#E11D48] text-white flex items-center justify-center shadow-xl hover:scale-110 transition-transform cursor-pointer"
                                >
                                  <Play size={20} className="fill-white ml-0.5" />
                                </button>
                              </div>
                            </>
                          )}
                          <div className="absolute top-3 left-3 pointer-events-none">
                            <span className="text-[10px] uppercase tracking-wider font-extrabold bg-[#E11D48] text-white px-2.5 py-0.5 rounded-full shadow-md flex items-center gap-1">
                              <Video size={10} /> Video Reportaje
                            </span>
                          </div>
                        </div>

                        <div className="p-5">
                          <Link to={getSlugLink(art)}>
                            <h3 className="font-heading text-lg font-bold text-white group-hover:text-[#E11D48] transition-colors leading-snug line-clamp-2">
                              {loc(art, "title")}
                            </h3>
                          </Link>
                          <p className="text-xs text-zinc-400 mt-2 line-clamp-2 leading-relaxed">
                            {loc(art, "excerpt")}
                          </p>
                        </div>
                      </div>

                      <div className="p-5 pt-0 flex items-center justify-between text-xs border-t border-white/5 mt-2">
                        <span className="text-zinc-500">{art.author_name}</span>
                        <Link to={getSlugLink(art)} className="text-[#E11D48] font-semibold flex items-center gap-1 hover:underline">
                          {t("readMore")} <ArrowRight size={12} />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* AD SLOT */}
            <div className="my-8"><AdSlot slot="7187293245" /></div>

            {/* SECTION 3: CRÍTICAS & ENSAYOS ESPECIALIZADOS */}
            {reviewsAndEssays.length > 0 && (
              <section className="space-y-6" data-testid="essays-section">
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <div className="flex items-center gap-2">
                    <FileText size={20} className="text-[#E11D48]" />
                    <h2 className="font-heading text-xl sm:text-2xl font-extrabold text-white">
                      {t("essaysSection")}
                    </h2>
                  </div>
                  <span className="text-xs text-zinc-400 font-medium">Análisis en profundidad</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {reviewsAndEssays.map((art) => (
                    <article
                      key={art.id}
                      className="group bg-zinc-900/60 border border-white/10 hover:border-[#E11D48]/40 rounded-xl overflow-hidden flex flex-col justify-between shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                    >
                      <Link to={getSlugLink(art)} className="flex flex-col h-full justify-between">
                        <div>
                          <div className="aspect-[16/10] overflow-hidden relative bg-zinc-950">
                            {art.cover_url ? (
                              <img
                                src={art.cover_url}
                                alt={loc(art, "title")}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                            ) : (
                              <div className="w-full h-full bg-zinc-800" />
                            )}
                            <div className="absolute top-3 left-3">
                              <span className="text-[10px] font-bold uppercase tracking-wider bg-black/80 backdrop-blur-md text-white px-2.5 py-0.5 rounded-full border border-white/10">
                                {art.category}
                              </span>
                            </div>
                          </div>

                          <div className="p-5">
                            <div className="flex items-center gap-2 text-[11px] text-zinc-400 mb-2">
                              <span className="text-zinc-300 font-semibold">{art.author_name}</span>
                              <span>•</span>
                              <span className="flex items-center gap-1 text-zinc-400">
                                <Clock size={11} /> {getReadTime(loc(art, "content"))}
                              </span>
                            </div>

                            <h3 className="font-heading text-lg font-bold text-white group-hover:text-[#E11D48] transition-colors leading-snug line-clamp-2">
                              {loc(art, "title")}
                            </h3>

                            <p className="text-xs text-zinc-400 mt-2.5 line-clamp-2 leading-relaxed">
                              {loc(art, "excerpt")}
                            </p>
                          </div>
                        </div>

                        <div className="p-5 pt-0 flex items-center justify-between text-xs text-zinc-500 font-medium">
                          <span>{art.published_at ? new Date(art.published_at).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }) : "Publicado"}</span>
                          <span className="text-[#E11D48] font-bold flex items-center gap-1">
                            Leer <ArrowRight size={12} />
                          </span>
                        </div>
                      </Link>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {/* SECTION 4: ENTREVISTAS & DIÁLOGOS */}
            {interviews.length > 0 && (
              <section className="space-y-6" data-testid="interviews-section">
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <div className="flex items-center gap-2">
                    <MessageSquare size={20} className="text-[#E11D48]" />
                    <h2 className="font-heading text-xl sm:text-2xl font-extrabold text-white">
                      {t("interviewsSection")}
                    </h2>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {interviews.map((art) => (
                    <Link
                      key={art.id}
                      to={getSlugLink(art)}
                      className="group bg-gradient-to-r from-zinc-900 via-zinc-900 to-black border border-white/10 hover:border-[#E11D48]/50 p-6 rounded-2xl flex flex-col sm:flex-row gap-6 items-center shadow-xl transition-all"
                    >
                      {art.cover_url && (
                        <div className="w-full sm:w-44 aspect-[4/3] rounded-xl overflow-hidden shrink-0 bg-zinc-950">
                          <img src={art.cover_url} alt={loc(art, "title")} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                      )}
                      <div className="flex-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-[#E11D48]/20 text-[#E11D48] border border-[#E11D48]/30 px-2.5 py-0.5 rounded-full">
                          {art.category}
                        </span>
                        <h3 className="font-heading text-lg font-bold text-white group-hover:text-[#E11D48] transition-colors mt-2 leading-snug">
                          {loc(art, "title")}
                        </h3>
                        <p className="text-xs text-zinc-400 mt-2 line-clamp-2 leading-relaxed">
                          {loc(art, "excerpt")}
                        </p>
                        <p className="text-xs font-semibold text-[#E11D48] mt-4 flex items-center gap-1">
                          Leer entrevista <ArrowRight size={12} />
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* ALL PUBLICATIONS GRID */}
            {restArticles.length > 0 && (
              <section className="space-y-6 pt-6 border-t border-white/10">
                <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2">
                  <Film size={18} className="text-[#E11D48]" />
                  <span>{t("allArticles")}</span>
                  <span className="text-xs font-semibold bg-zinc-800 text-zinc-400 px-2.5 py-0.5 rounded-full">
                    {restArticles.length}
                  </span>
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {restArticles.map((art) => (
                    <article
                      key={art.id}
                      className="group bg-zinc-900/60 border border-white/10 hover:border-[#E11D48]/40 rounded-xl overflow-hidden flex flex-col justify-between shadow-lg hover:shadow-xl transition-all"
                    >
                      <Link to={getSlugLink(art)} className="flex flex-col h-full justify-between">
                        <div>
                          <div className="aspect-[16/10] overflow-hidden relative bg-zinc-950">
                            {art.cover_url ? (
                              <img src={art.cover_url} alt={loc(art, "title")} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            ) : (
                              <div className="w-full h-full bg-zinc-800" />
                            )}
                            <div className="absolute top-3 left-3">
                              <span className="text-[10px] font-bold uppercase tracking-wider bg-black/80 backdrop-blur-md text-white px-2.5 py-0.5 rounded-full border border-white/10">
                                {art.category}
                              </span>
                            </div>
                          </div>

                          <div className="p-5">
                            <h3 className="font-heading text-base font-bold text-white group-hover:text-[#E11D48] transition-colors leading-snug line-clamp-2">
                              {loc(art, "title")}
                            </h3>
                            <p className="text-xs text-zinc-400 mt-2 line-clamp-2 leading-relaxed">
                              {loc(art, "excerpt")}
                            </p>
                          </div>
                        </div>

                        <div className="p-5 pt-0 flex items-center justify-between text-xs text-zinc-500">
                          <span>{art.author_name}</span>
                          <span className="text-[#E11D48] font-bold flex items-center gap-1">
                            Leer <ArrowRight size={12} />
                          </span>
                        </div>
                      </Link>
                    </article>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

